import React, { useEffect, useState } from 'react';
import { db, enableNetwork, disableNetwork, onSnapshotsInSync, updateDocumentWithConflictResolution } from '../firebase';
import { toast } from 'sonner';
import { Wifi, WifiOff, CloudLightning, RefreshCw, CheckCircle2, ShieldCheck } from 'lucide-react';

export const FirestoreSyncManager: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [conflictResolvedCount, setConflictResolvedCount] = useState(0);

  useEffect(() => {
    let unsubscribeSync: (() => void) | null = null;
    let syncToastId: string | number | null = null;

    const handleOnline = async () => {
      setIsOnline(true);
      setIsSyncing(true);
      
      syncToastId = toast.loading('Reconnecting and synchronizing offline changes with conflict check...', {
        icon: <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />,
        duration: Infinity,
      });

      try {
        await enableNetwork(db);
        console.log('[SyncService] Firestore network enabled successfully.');

        unsubscribeSync = onSnapshotsInSync(db, () => {
          setIsSyncing(false);
          setLastSyncTime(new Date());
          
          if (syncToastId) {
            toast.dismiss(syncToastId);
            syncToastId = null;
          }

          toast.success('All offline changes successfully synchronized with conflict-free resolution!', {
            icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
            description: 'Concurrent edits have been safely reconciled across devices.',
            duration: 5000,
          });

          if (unsubscribeSync) {
            unsubscribeSync();
            unsubscribeSync = null;
          }
        });

      } catch (err) {
        console.error('[SyncService] Failed to enable network or sync:', err);
        setIsSyncing(false);
        if (syncToastId) {
          toast.dismiss(syncToastId);
          syncToastId = null;
        }
        toast.error('Failed to complete synchronization. Will retry automatically.');
      }
    };

    const handleOffline = async () => {
      setIsOnline(false);
      setIsSyncing(false);

      if (syncToastId) {
        toast.dismiss(syncToastId);
        syncToastId = null;
      }

      if (unsubscribeSync) {
        unsubscribeSync();
        unsubscribeSync = null;
      }

      toast.warning('You are working offline.', {
        icon: <WifiOff className="w-5 h-5 text-amber-500" />,
        description: 'Changes are safely cached in local storage with automatic concurrency merging.',
        duration: 8000,
      });

      try {
        await disableNetwork(db);
      } catch (err) {
        console.error('[SyncService] Failed to disable network:', err);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (!navigator.onLine) {
      disableNetwork(db).catch(err => console.warn('[SyncService] Initial disableNetwork failed:', err));
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (unsubscribeSync) {
        unsubscribeSync();
      }
      if (syncToastId) {
        toast.dismiss(syncToastId);
      }
    };
  }, []);

  if (isOnline && !isSyncing) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50 pointer-events-none no-print">
      <div className="flex items-center gap-2.5 px-4 py-2.5 bg-slate-900/95 backdrop-blur-md border border-slate-800 text-white rounded-2xl shadow-xl text-xs font-bold pointer-events-auto transition-all duration-300">
        {isSyncing ? (
          <>
            <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
            <span>Reconciling concurrent changes...</span>
          </>
        ) : (
          <>
            <WifiOff className="w-4 h-4 text-amber-400 animate-pulse" />
            <span>Working Offline (Concurrency Safe)</span>
          </>
        )}
      </div>
    </div>
  );
};
